'use client';

import { useState, useEffect, useCallback } from 'react';
import { Badge } from '@/components/ui/badge';
import { MonoIcon } from '@/components/ui/MonoIcon';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { workspaceApi } from '@/lib/api/workspace';
import { ticketApi } from '@/lib/api/ticket/ticketApi';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useProjectStore } from '@/stores/project-store';
import type { Tag as TagType, Priority, Ticket, TicketUpdate } from 'dico-types';
import { cn } from '@/lib/utils';
import { getPickerIcon } from '@/lib/picker-icons';
import { buttonVariants } from '../ui/button';

// ─── color dot ───────────────────────────────────────────────────────────────

function ColorDot({
  color,
  icon,
  className
}: {
  color?: string;
  icon?: string;
  className?: string;
}) {
  // If there's an icon, show it (with optional color styling)
  const iconType = getPickerIcon(icon);
  if (iconType) {
    return (
      <MonoIcon
        type={iconType}
        className={cn('h-3.5 w-3.5', className)}
        style={color ? { color } : undefined}
      />
    );
  }

  // If there's a color but no icon, show a colored dot
  if (color) {
    return (
      <span
        className={cn('inline-block h-2 w-2 shrink-0 rounded-full', className)}
        style={{ backgroundColor: color }}
      />
    );
  }

  // Fallback: show default Circle icon
  return <MonoIcon type="Circle" className={cn('text-muted-foreground h-3.5 w-3.5', className)} />;
}

// ─── single-select dropdown ───────────────────────────────────────────────────

interface SingleSelectOption {
  id: string;
  name: string;
  color?: string;
  icon?: string;
}

function SingleSelectField({
  label,
  selectedId,
  options,
  placeholder,
  onSelect,
  disabled
}: {
  label: string;
  selectedId: string | null | undefined;
  options: SingleSelectOption[];
  placeholder: string;
  onSelect: (id: string | null) => void;
  disabled?: boolean;
}) {
  const selected = options.find((o) => o.id === selectedId);

  return (
    <div className="flex w-full items-center gap-1">
      <span className="basis-14 text-sm font-medium">{label}</span>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            !selected && 'text-muted-foreground'
          )}
        >
          {selected ? (
            <span className="flex items-center gap-1.5">
              <ColorDot color={selected.color} icon={selected.icon} />
              {selected.name}
            </span>
          ) : (
            placeholder
          )}
          <MonoIcon type="ChevronDown" className="text-muted-foreground" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start" className="min-w-40">
          <DropdownMenuRadioGroup
            value={selectedId ?? ''}
            onValueChange={(value) => onSelect(value || null)}
          >
            {options.map((opt) => (
              <DropdownMenuRadioItem key={opt.id} value={opt.id}>
                <ColorDot color={opt.color} icon={opt.icon} />
                {opt.name}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
          {selectedId && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-muted-foreground" onSelect={() => onSelect(null)}>
                Clear
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ─── multi-select (tags) ──────────────────────────────────────────────────────

function TagsField({
  tags,
  selectedIds,
  onToggle,
  disabled
}: {
  tags: TagType[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  disabled?: boolean;
}) {
  const selectedTags = tags.filter((t) => selectedIds.includes(t.id));

  return (
    <div className="flex w-full items-center gap-1">
      <span className="basis-14 text-sm font-medium">Tags</span>
      <DropdownMenu>
        <DropdownMenuTrigger
          disabled={disabled}
          className={cn(
            buttonVariants({ variant: 'outline' }),
            'px-0.5',
            selectedTags.length === 0 && 'text-muted-foreground'
          )}
        >
          {selectedTags.length === 0 ? (
            <span className="flex items-center gap-1.5">
              <MonoIcon type="Tag" className="text-muted-foreground" />
              No tags
            </span>
          ) : (
            <span className="flex flex-wrap items-center gap-1">
              {selectedTags.map((t) => (
                <Badge key={t.id} variant="outline">
                  {t.name}
                </Badge>
              ))}
            </span>
          )}
          <MonoIcon type="ChevronDown" className="text-muted-foreground ml-1 shrink-0" />
        </DropdownMenuTrigger>
        <DropdownMenuContent side="bottom" align="start" className="min-w-40">
          {tags.map((tag) => (
            <DropdownMenuCheckboxItem
              key={tag.id}
              checked={selectedIds.includes(tag.id)}
              onCheckedChange={() => onToggle(tag.id)}
            >
              {tag.name}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

// ─── panel ────────────────────────────────────────────────────────────────────

interface TicketMetaPanelProps {
  workspaceId: string;
  ticket: Ticket;
  /** Called with the patched fields after a successful save — no page reload needed. */
  onTicketPatch: (patch: Partial<Ticket>) => void;
}

export function TicketMetaPanel({ workspaceId, ticket, onTicketPatch }: TicketMetaPanelProps) {
  const getStatuses = useWorkspaceStore((s) => s.getStatuses);
  const statuses = getStatuses(workspaceId);
  const getProjects = useProjectStore((s) => s.getProjects);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const projects = getProjects(workspaceId);

  const [tags, setTags] = useState<TagType[]>([]);
  const [priorities, setPriorities] = useState<Priority[]>([]);

  // Local optimistic state — initialised from ticket, synced only on ticket.id change
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>(ticket.tagIds ?? []);
  const [selectedStatusId, setSelectedStatusId] = useState<string | null | undefined>(
    ticket.statusId
  );
  const [selectedPriorityId, setSelectedPriorityId] = useState<string | null | undefined>(
    ticket.priorityId
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null | undefined>(
    ticket.projectId
  );

  useEffect(() => {
    setSelectedTagIds(ticket.tagIds ?? []);
    setSelectedStatusId(ticket.statusId);
    setSelectedPriorityId(ticket.priorityId);
    setSelectedProjectId(ticket.projectId);
    // Only re-sync when navigating to a different ticket
  }, [ticket.id]);

  useEffect(() => {
    workspaceApi.getWorkspaceTags(workspaceId).then((r) => setTags(r.data ?? []));
    workspaceApi.getWorkspacePriorities(workspaceId).then((r) => setPriorities(r.data ?? []));
    if (projects.length === 0) {
      fetchProjects(workspaceId);
    }
  }, [workspaceId, projects.length]);

  /** Persist a patch. On failure, revert the provided rollback values. */
  const save = useCallback(
    async (patch: TicketUpdate, rollback: () => void) => {
      try {
        await ticketApi.updateTicket(workspaceId, ticket.id, patch);
        onTicketPatch(patch as Partial<Ticket>);
      } catch {
        rollback();
      }
    },
    [workspaceId, ticket.id, onTicketPatch]
  );

  const handleToggleTag = useCallback(
    (tagId: string) => {
      const prev = selectedTagIds;
      const next = prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId];
      setSelectedTagIds(next);
      save({ tagIds: next }, () => setSelectedTagIds(prev));
    },
    [selectedTagIds, save]
  );

  const handleSetStatus = useCallback(
    (statusId: string | null) => {
      const prev = selectedStatusId;
      setSelectedStatusId(statusId);
      save({ statusId }, () => setSelectedStatusId(prev));
    },
    [selectedStatusId, save]
  );

  const handleSetPriority = useCallback(
    (priorityId: string | null) => {
      const prev = selectedPriorityId;
      setSelectedPriorityId(priorityId);
      save({ priorityId }, () => setSelectedPriorityId(prev));
    },
    [selectedPriorityId, save]
  );

  const handleSetProject = useCallback(
    (projectId: string | null) => {
      const prev = selectedProjectId;
      setSelectedProjectId(projectId);
      save({ projectId }, () => setSelectedProjectId(prev));
    },
    [selectedProjectId, save]
  );

  return (
    <div className="">
      <div className="flex flex-col flex-wrap items-start gap-2">
        <SingleSelectField
          label="Status"
          selectedId={selectedStatusId}
          options={statuses.map((s) => ({ id: s.id, name: s.name, color: s.color, icon: s.icon }))}
          placeholder="No status"
          onSelect={handleSetStatus}
        />
        <SingleSelectField
          label="Project"
          selectedId={selectedProjectId}
          options={projects.map((p) => ({
            id: p.id,
            name: p.name,
            color: p.color || undefined,
            icon: p.icon || undefined
          }))}
          placeholder="No project"
          onSelect={handleSetProject}
        />
        <SingleSelectField
          label="Priority"
          selectedId={selectedPriorityId}
          options={priorities.map((p) => ({
            id: p.id,
            name: p.name,
            color: p.color,
            icon: p.icon
          }))}
          placeholder="No priority"
          onSelect={handleSetPriority}
        />
        <TagsField tags={tags} selectedIds={selectedTagIds} onToggle={handleToggleTag} />
      </div>
    </div>
  );
}
